import { inject } from 'aurelia';
import { IHttpClient, json } from '@aurelia/fetch-client';

import { apiEndpoint, websiteShortCode } from '../environment';

import { ApiInterceptor } from './api-interceptor';

@inject(IHttpClient, ApiInterceptor)
export class ApiService {
    constructor(readonly http: IHttpClient, readonly interceptor: ApiInterceptor) {
        http.configure(config =>
            config.withBaseUrl(apiEndpoint() as string)
                .withInterceptor(
                    {
                        // @ts-expect-error
                        request(request) {
                            return interceptor.request(request);
                        },
                        // @ts-expect-error
                        response(response) {
                            return interceptor.response(response);
                        }
                    }
                )
                .withDefaults({
                    headers: {
                        'Accept': 'application/json'
                    }
                })
        );
    }

    maxRetries = 3;

    async _request<T>(path: string, options: RequestInit | undefined) {
        let attempt = 0;

        while (attempt < this.maxRetries) {
            const result = await this.http.fetch(path, options);

            if (!result && result === null) {
                attempt++;
                await new Promise(resolve => setTimeout(resolve, 1000));
                continue;
            }

            if (result) {
                const status = result.status;

                if (status === 204) {
                    return null;
                }

                if (status >= 400) {
                    return null;
                }

                let response: T | null = null;

                if (['Health', 'antiforgery'].some(x => result.url?.includes(x))) {
                    response = result.ok as T;
                } else if (result) {
                    response = await result.json() as T;
                }

                if (status >= 200 && status < 400) {
                    return response;
                }
            }

            return null;
        }

        return null;
    }

    doGet<T>(path: string, params?: Record<string, string>) {
        const options: RequestInit = {
            method: 'GET',
            headers: {
                websiteShortCode: websiteShortCode(),
            }
        };

        if (params) {
            path += `?${new URLSearchParams(params).toString()}`;
        }

        return this._request<T>(path, options);
    }

    doPatch<T>(path: string, body) {
        const options = {
            method: 'PATCH',
            body: json(body)
        };

        return this._request<T>(path, options);
    }

    doPost<T>(path: string, body, isFile = false, withCredentials = false) {
        return this._push<T>(path, body, false, isFile, withCredentials);
    }

    doPut<T>(path: string, body) {
        return this._push<T>(path, body, true);
    }

    _fileUpload<T>(path: string, formData: FormData) {
        const options = {
            method: 'POST',
            body: formData
        };

        return this._request<T>(path, options);
    }

    async _fileDownload(path: string) {
        const options = {
            method: 'GET'
        };

        let attempt = 0;

        while (attempt < this.maxRetries) {
            const result = await this.http.fetch(path, options);

            if (!result && result === null) {
                attempt++;
                await new Promise(resolve => setTimeout(resolve, 1000));
                continue;
            }

            if (result) {
                const status = result.status;
                if (status === 204) {
                    return null;
                }

                if (status > 400) {
                    return null;
                }

                let response;

                if (result) {
                    response = await result.blob();
                }

                if (status >= 200 && status < 400) {
                    return response as Blob;
                }
            }

            return null;
        }

        return null;
    }

    doDelete<T>(path: string, body?: any) {
        const options: RequestInit = {
            method: 'DELETE'
        };

        if (body) options.body = json(body);

        return this._request<T>(path, options);
    }

    _push<T>(path: string, body, asPut = false, isFile = false, withCredentials = false) {
        const options: RequestInit = {
            method: asPut ? 'PUT' : 'POST',
            body: (isFile ? body : json(body)) as BodyInit
        };

        if (withCredentials) options.credentials = 'include';

        return this._request<T>(path, options);
    }
}
